import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Stack } from '@mui/material'

import { Button, Modal } from '@repo/ui'
import { AnnouncementCard, useAnnouncementCta } from 'components/display'
import { useAnnouncementFeed, useMarkAnnouncementSeen } from 'hooks/api'
import { useChecklistVisibility } from 'hooks'
import { ROUTES } from 'routes'

/**
 * Session key for "a What's-new modal has already been shown in this tab".
 *
 * `sessionStorage`, not `localStorage` and not React state:
 *   - React state alone resets on every navigation that remounts Layout, so a user
 *     clicking around would meet the dialog repeatedly within one visit.
 *   - `localStorage` would persist across sessions, which is the SERVER's job — the
 *     `announcement_seen` row is the permanent record, and duplicating permanence in
 *     the client means the two can disagree (clear your browser data and the server
 *     still says seen; that is correct and this key must not override it).
 *
 * So: the server owns "has this user ever seen it", this key owns "have we already
 * interrupted them once in this tab".
 */
const SESSION_KEY = 'vera:whats-new-modal-shown'

/**
 * The once-per-session "What's new" dialog (VRG-221).
 *
 * Enforces the harder half of rule 1 — NEVER SHOW HISTORY AS A QUEUE — on the client
 * side. The server already guarantees there is at most ONE candidate (`unseenLatest`,
 * filtered by the audience gate and by "published after this user signed up"), so
 * there is no list here to iterate. This component adds the second guarantee: AT MOST
 * ONE MODAL PER SESSION, even if a second announcement is published while the tab is
 * open, and even if the user never dismisses this one.
 *
 * That second guarantee is separate from the first and both are needed. Without the
 * server rule, a new user would meet the entire backlog. Without this session rule, a
 * user who closes the dialog with the backdrop (which does NOT mark it seen — see
 * below) would meet it again on their next navigation, which is how a helpful notice
 * becomes something people learn to click past.
 *
 * WHY THE BACKDROP DOES NOT MARK IT SEEN: dismissal is permanent and `/whats-new` is
 * the only way back. An accidental backdrop click permanently burning an announcement
 * is a worse failure than showing it once more, so only the explicit "Got it" button
 * (and taking the CTA, which is a stronger acknowledgement than "Got it") writes the
 * seen row. The session key still stops it re-appearing in this tab.
 */
export const AnnouncementModal = () => {
  const navigate = useNavigate()
  const { data } = useAnnouncementFeed()
  const markSeen = useMarkAnnouncementSeen()
  const [open, setOpen] = useState(false)
  const [shown, setShown] = useState<string | null>(null)

  const { isChecklistLive, isLoading: checklistLoading } = useChecklistVisibility()

  const candidate = data?.unseenLatest ?? null

  useEffect(() => {
    if (!candidate) return
    /*
      VRG-338: NOT WHILE THE USER IS STILL BEING ONBOARDED.

      A first-run brand has not finished finding out what the product does; interrupting that
      with news about a feature they have never seen is noise at best, and at worst it teaches
      them on day one that Vera's dialogs are things to click past. So the What's-new modal
      waits until the SETUP CHECKLIST has retired — completed, or dismissed. (It used to wait
      on Home retiring; Home is permanent now, so the checklist is the signal that onboarding
      is behind them.)

      Deliberately checks BEFORE the session key is written. Suppressing and marking-as-shown
      in the same pass would burn this tab's one interruption on a modal that never appeared,
      so the announcement would stay silently unshown for the rest of the session even after
      the user finished setting up. Nothing is marked seen here either: this defers the
      announcement, it does not consume it.

      The loading flag is part of the gate, not a detail — `isChecklistLive` is false while the
      six checklist queries settle, so acting on it early would let the dialog through on
      exactly the cold load this is meant to protect.
    */
    if (checklistLoading || isChecklistLive) return
    // Already interrupted this tab once — the page carries the rest.
    if (sessionStorage.getItem(SESSION_KEY)) return
    // Guard against the feed refetching and re-firing this effect for the same row.
    if (shown === candidate.id) return

    sessionStorage.setItem(SESSION_KEY, candidate.id)
    setShown(candidate.id)
    setOpen(true)
  }, [candidate, shown, isChecklistLive, checklistLoading])

  /**
   * Explicit acknowledgement — the only path that writes the permanent seen row.
   *
   * Declared ABOVE the `!candidate` early return, because `useAnnouncementCta` below is
   * a hook and cannot follow one. Hence the null guard on the mutate.
   */
  const acknowledge = () => {
    setOpen(false)
    if (candidate) markSeen.mutate(candidate.id)
  }

  /*
    The CTA the CARD used to render in its body, now rendered by this dialog's footer.

    Same button, same label, same handler — `useAnnouncementCta` is shared with the card
    precisely so the two cannot drift, which matters more here than anywhere: dismissal
    is permanent, so if this dialog ever offered a path the archive did not, a reader who
    dismissed it would have lost the feature for good. See `AnnouncementCard`'s docblock
    for why moving the button out of the card does not weaken that guarantee.

    `acknowledge` as `onActioned`: taking the CTA is a STRONGER acknowledgement than
    "Got it", so it writes the seen row too, and closes the dialog before navigating so
    it is not left covering the page the reader was just sent to.
  */
  const { hasCta, ctaLabel, onCta } = useAnnouncementCta(candidate, acknowledge)

  if (!candidate) return null

  return (
    <Modal
      open={open}
      // Backdrop / escape. Closes without marking seen, on purpose (see the docblock).
      onClose={() => setOpen(false)}
      title="What's new"
      actions={
        /*
          Left → right: See all · Got it · the CTA (§26/§33 — exactly one primary, and it
          is the rightmost control).

          `space-between` rather than one right-aligned row: "See all" is not part of the
          decision this dialog is asking for. It is an escape hatch to the archive, so it
          sits apart from the two buttons that resolve the announcement, in the corner
          the eye reaches last. `text` for the same reason — it is the quietest thing in
          the footer because it is the least likely thing you want.
        */
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          {/* Route to the archive, so the dialog is not a dead end for someone who
              wants the rest of the history. Marks this one seen on the way out — they
              are choosing to read the list, which is a deliberate act, not an
              accidental backdrop click. */}
          <Button
            variant="text"
            onClick={() => {
              acknowledge()
              navigate(ROUTES.WHATS_NEW)
            }}
          >
            See all
          </Button>

          {/* 12px between a button pair acting as a unit (§29, on the 4px @repo/ui
              scale). These two ARE the pair: both resolve the announcement. */}
          <Stack direction="row" spacing={3} alignItems="center">
            {/*
              "GOT IT" IS LOAD-BEARING AND MUST NOT BE REMOVED — it is not redundant with
              the X, and that is the whole point. Dismissal is permanent and `/whats-new`
              is the only way back, so the backdrop and the close button deliberately do
              NOT write the seen row (see the docblock). Delete this button and a reader
              who does not want the CTA has no way to acknowledge at all: the X closes
              without recording anything, so the announcement returns every session until
              they take an action they never wanted.

              It steps down to `secondary` when a CTA exists, because a surface gets one
              primary and the CTA is the stronger claim. With no CTA it IS the action of
              the dialog, so it takes the primary slot.
            */}
            <Button variant={hasCta ? 'secondary' : 'primary'} onClick={acknowledge}>
              Got it
            </Button>
            {hasCta && (
              <Button variant="primary" onClick={onCta}>
                {ctaLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    >
      {/* The SAME card the /whats-new page renders (§36) — only the chrome differs. */}
      <AnnouncementCard announcement={candidate} variant="modal" onActioned={acknowledge} />
    </Modal>
  )
}
